import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import Loading from './components/Loading';
import AnalyticsTracker from './components/AnalyticsTracker';
import ErrorBoundary from './components/ErrorBoundary';
import ScrollToTop from './components/ScrollToTop';

// Lazy load all pages for code splitting
const Home = lazy(() => import('./pages/Home'));
const Services = lazy(() => import('./pages/Services'));
const Projects = lazy(() => import('./pages/Projects'));
const About = lazy(() => import('./pages/About'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const CostCalculator = lazy(() => import('./pages/CostCalculator'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const FAQs = lazy(() => import('./pages/FAQs'));
const ReturnRefundPolicy = lazy(() => import('./pages/ReturnRefundPolicy'));
const IndividualResidentialHomes = lazy(() => import('./pages/services/IndividualResidentialHomes'));
const RentalHomes = lazy(() => import('./pages/services/RentalHomes'));
const DuplexHouses = lazy(() => import('./pages/services/DuplexHouses'));
const GPlusStructures = lazy(() => import('./pages/services/GPlusStructures'));
const LuxuryVillaHomes = lazy(() => import('./pages/services/LuxuryVillaHomes'));
const PlotDevelopment = lazy(() => import('./pages/services/PlotDevelopment'));
const Renovations = lazy(() => import('./pages/services/Renovations'));
const CommercialBuilds = lazy(() => import('./pages/services/CommercialBuilds'));
const ClientDashboard = lazy(() => import('./pages/ClientDashboard'));
const ClientProjectDetail = lazy(() => import('./pages/ClientProjectDetail'));
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard'));
const ProjectsList = lazy(() => import('./pages/admin/ProjectsList'));
const ProjectForm = lazy(() => import('./pages/admin/ProjectForm'));
const ProjectDetail = lazy(() => import('./pages/admin/ProjectDetail'));
const BulkProjectImport = lazy(() => import('./pages/admin/BulkProjectImport'));
const TestimonialsList = lazy(() => import('./pages/admin/TestimonialsList'));
const TestimonialForm = lazy(() => import('./pages/admin/TestimonialForm'));
const ProjectPhotos = lazy(() => import('./pages/admin/ProjectPhotos'));
const ClientAccounts = lazy(() => import('./pages/admin/ClientAccounts'));
const Leads = lazy(() => import('./pages/admin/Leads'));
const ProjectPublicView = lazy(() => import('./pages/ProjectPublicView'));

function App() {
  return (
    <ErrorBoundary>
      <Router>
        <ScrollToTop />
        <AnalyticsTracker />
        <Suspense fallback={<Loading />}>
          <Routes>
        {/* Public Project View (no layout, read-only) */}
        <Route path="/project-status/:token" element={<ProjectPublicView />} />
        
        {/* Public Routes with Layout */}
        <Route path="/" element={<Layout><Home /></Layout>} />
        <Route path="/services" element={<Layout><Services /></Layout>} />
        <Route path="/services/individual-residential-homes" element={<Layout><IndividualResidentialHomes /></Layout>} />
        <Route path="/services/rental-homes" element={<Layout><RentalHomes /></Layout>} />
        <Route path="/services/duplex-houses" element={<Layout><DuplexHouses /></Layout>} />
        <Route path="/services/g-plus-structures" element={<Layout><GPlusStructures /></Layout>} />
        <Route path="/services/luxury-villa-homes" element={<Layout><LuxuryVillaHomes /></Layout>} />
        <Route path="/services/plot-development" element={<Layout><PlotDevelopment /></Layout>} />
        <Route path="/services/renovations" element={<Layout><Renovations /></Layout>} />
        <Route path="/services/commercial-builds" element={<Layout><CommercialBuilds /></Layout>} />
        <Route path="/projects" element={<Layout><Projects /></Layout>} />
        <Route path="/cost-calculator" element={<Layout><CostCalculator /></Layout>} />
        <Route path="/about" element={<Layout><About /></Layout>} />
        <Route path="/contact" element={<Layout><Contact /></Layout>} />
        <Route path="/terms-and-conditions" element={<Layout><TermsAndConditions /></Layout>} />
        <Route path="/privacy-policy" element={<Layout><PrivacyPolicy /></Layout>} />
        <Route path="/faqs" element={<Layout><FAQs /></Layout>} />
        <Route path="/return-refund-policy" element={<Layout><ReturnRefundPolicy /></Layout>} />
        
        {/* Auth Routes without Layout */}
        <Route path="/login" element={<Login />} />
        
        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/dashboard"
          element={
            <AdminProtectedRoute>
              <AdminDashboard />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/projects"
          element={
            <AdminProtectedRoute>
              <ProjectsList />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/projects/new"
          element={
            <AdminProtectedRoute>
              <ProjectForm />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/projects/bulk-import"
          element={
            <AdminProtectedRoute>
              <BulkProjectImport />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/projects/:id/edit"
          element={
            <AdminProtectedRoute>
              <ProjectForm />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/projects/:id"
          element={
            <AdminProtectedRoute>
              <ProjectDetail />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/testimonials"
          element={
            <AdminProtectedRoute>
              <TestimonialsList />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/testimonials/new"
          element={
            <AdminProtectedRoute>
              <TestimonialForm />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/testimonials/:id"
          element={
            <AdminProtectedRoute>
              <TestimonialForm />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/project-photos"
          element={
            <AdminProtectedRoute>
              <ProjectPhotos />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/client-accounts"
          element={
            <AdminProtectedRoute>
              <ClientAccounts />
            </AdminProtectedRoute>
          }
        />
        <Route
          path="/admin/leads"
          element={
            <AdminProtectedRoute>
              <Leads />
            </AdminProtectedRoute>
          }
        />
        
        {/* Protected Client Routes */}
        <Route
          path="/client/dashboard"
          element={
            <ProtectedRoute>
              <ClientDashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/client/project/:id"
          element={
            <ProtectedRoute>
              <ClientProjectDetail />
            </ProtectedRoute>
          }
        />
          </Routes>
        </Suspense>
      </Router>
    </ErrorBoundary>
  );
}

export default App;

